import {
  ProviderFacetGroups,
  ProviderFilterGroups,
  ProviderSpecialty,
  ProviderHospital,
  ProviderClinic,
  ProviderLanguage,
  ProviderInsurance,
  ProviderFilterTitles
} from 'store/providerSearch/types';
import { currentLocation } from 'store/providerSearch/constants';
import { Insurance } from 'store/profile/types';
import { Filter } from '../types';

export const insuranceAliasValues = ({ insuranceName, networkId, planId }: Insurance) => ({
  title: insuranceName.toUpperCase(),
  group: ProviderFacetGroups.insuranceAliasValues,
  value: networkId || planId
});

//
//  Constant: filters
//  Description: The structure to use for both mobile and web Provider Search Filters
//
export const filters = {
  main: {
    title: ProviderFilterTitles.main,
    values: {
      acceptsMyInsurance: {
        title: ProviderFilterTitles.acceptsMyInsurance,
        group: 'acceptsMyInsurance',
        value: true,
        values: {
          insuranceAliasValues,
          insurancePlanNcid: {
            title: ProviderFilterTitles.insurancePlans,
            values: (slug: ProviderInsurance[]) =>
              slug.reduce((acc, { planId, planName }) => {
                return {
                  ...acc,
                  [planId]: {
                    title: planName,
                    group: ProviderFacetGroups.insurancePlanNcid,
                    value: planId
                  }
                };
              }, {} as { [key: string]: Filter })
          }
        }
      },
      hasOnlineNpvSlots: {
        title: ProviderFilterTitles.hasOnlineNpvSlots,
        group: ProviderFilterGroups.hasOnlineNpvSlots,
        value: true
      },
      isAcceptingOnlineBooking: {
        title: ProviderFilterTitles.isAcceptingOnlineBooking,
        group: ProviderFilterGroups.isAcceptingOnlineBooking,
        value: true
      },
      acceptsChildren: {
        title: ProviderFilterTitles.acceptsChildren,
        group: ProviderFilterGroups.acceptsChildren,
        value: true
      },
      lgtbqFlag: {
        title: ProviderFilterTitles.lgtbqFlagFilter,
        group: ProviderFilterGroups.lgtbqFlag,
        value: true
      }
    }
  },
  locationNearMe: {
    title: ProviderFilterTitles.locationNearMe,
    values: {
      locationText: {
        title: ProviderFilterTitles.location,
        values: {
          manual: {
            title: ProviderFilterTitles.locationManual,
            group: 'locationText',
            value: ''
          },
          auto: {
            title: ProviderFilterTitles.locationAuto,
            group: 'locationText',
            value: currentLocation
          }
        }
      },
      locationDistance: {
        title: ProviderFilterTitles.distanceRange,
        values: {
          fiveMiles: {
            title: ProviderFilterTitles.distanceRangeSm,
            group: ProviderFacetGroups.locationDistance,
            value: 5
          },
          tenMiles: {
            title: ProviderFilterTitles.distanceRangeMd,
            group: ProviderFacetGroups.locationDistance,
            value: 10
          },
          twentyFiveMiles: {
            title: ProviderFilterTitles.distanceRangeLg,
            group: ProviderFacetGroups.locationDistance,
            value: 25
          },
          any: {
            title: ProviderFilterTitles.distanceRangeAny,
            group: ProviderFacetGroups.locationDistance,
            value: 9999
          }
        }
      }
    }
  },
  specialties: {
    title: ProviderFilterTitles.specialties,
    values: (slug: ProviderSpecialty[]) =>
      slug.reduce((acc, { value }) => {
        return {
          ...acc,
          [value]: {
            title: value,
            group: ProviderFacetGroups.specialty,
            value
          }
        };
      }, {} as { [key: string]: Filter })
  },
  gender: {
    title: ProviderFilterTitles.gender,
    values: {
      female: {
        title: ProviderFilterTitles.genderFemale,
        group: ProviderFacetGroups.gender,
        value: 'F'
      },
      male: {
        title: ProviderFilterTitles.genderMale,
        group: ProviderFacetGroups.gender,
        value: 'M'
      }
    }
  },
  hasPhotoVideo: {
    title: ProviderFilterTitles.hasPhotoVideo,
    values: {
      hasPhoto: {
        title: ProviderFilterTitles.hasPhoto,
        group: ProviderFilterGroups.hasPhoto,
        value: true
      },
      hasVideo: {
        title: ProviderFilterTitles.hasAboutVideo,
        group: ProviderFilterGroups.hasAboutVideo,
        value: true
      }
    }
  },
  organizationAffiliations: {
    title: ProviderFilterTitles.organizationAffiliations,
    values: {
      isStLukes: {
        title: ProviderFilterTitles.stLukes,
        group: ProviderFilterGroups.isStLukes,
        value: true
      },
      isMyHealth: {
        title: ProviderFilterTitles.myHealth,
        group: ProviderFilterGroups.isMyHealth,
        value: true
      },
      isMedicalGroup: {
        title: ProviderFilterTitles.medicalGroup,
        group: ProviderFilterGroups.isMedicalGroup,
        value: true
      }
    }
  },
  affiliatedHospitals: {
    title: ProviderFilterTitles.affiliatedHospitals,
    values: (slug: ProviderHospital[]) =>
      slug.reduce((acc, { hospitalName, locationId }) => {
        return {
          ...acc,
          [locationId]: {
            title: hospitalName,
            group: ProviderFacetGroups.hospitalAffiliationList,
            value: locationId
          }
        };
      }, {} as { [key: string]: Filter })
  },
  clinics: {
    title: ProviderFilterTitles.clinics,
    values: (slug: ProviderClinic[]) =>
      slug.reduce((acc, { clinicName, clinicId }) => {
        return {
          ...acc,
          [clinicId]: {
            title: clinicName,
            group: ProviderFacetGroups.intermountainClinics,
            value: clinicId
          }
        };
      }, {} as { [key: string]: Filter })
  },
  languages: {
    title: ProviderFilterTitles.languagesSpoken,
    values: (slug: ProviderLanguage[]) =>
      slug.reduce((acc, { languageDescription }) => {
        return {
          ...acc,
          [languageDescription]: {
            title: languageDescription,
            group: ProviderFacetGroups.language,
            value: languageDescription
          }
        };
      }, {} as { [key: string]: Filter })
  }
} as const;
